import { useTheme } from '../ui/ThemeProvider';

interface NumpadProps {
  onDigit: (digit: number) => void;
  onDelete: () => void;
  onSubmit: () => void;
  canSubmit: boolean;
  disabled?: boolean;
}

export function Numpad({ onDigit, onDelete, onSubmit, canSubmit, disabled = false }: NumpadProps) {
  const { theme } = useTheme();

  const digitRows = [
    [1, 2, 3],
    [4, 5, 6],
    [7, 8, 9],
  ];

  const buttonBaseStyle = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.lg,
    fontWeight: theme.typography.fontWeight.bold,
    borderRadius: theme.borders.radius.md,
    border: `2px solid ${theme.colours.background.tertiary}`,
    backgroundColor: theme.colours.background.elevated,
    color: theme.colours.text.primary,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    transition: `all ${theme.transitions.fast}`,
    padding: theme.spacing.md,
    minWidth: '60px',
    minHeight: '60px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const digitButtonStyle = {
    ...buttonBaseStyle,
    '&:hover:not(:disabled)': {
      backgroundColor: theme.colours.background.tertiary,
      borderColor: theme.colours.accent.primary,
    },
  };

  return (
    <div
      className="numpad"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing.sm,
        padding: theme.spacing.md,
        backgroundColor: theme.colours.background.secondary,
        borderRadius: theme.borders.radius.lg,
        userSelect: 'none',
      }}
    >
      {digitRows.map((row, rowIndex) => (
        <div
          key={rowIndex}
          className="numpad__row"
          style={{
            display: 'flex',
            gap: theme.spacing.sm,
            justifyContent: 'center',
          }}
        >
          {row.map((digit) => (
            <button
              key={digit}
              className="numpad__digit"
              onClick={() => onDigit(digit)}
              disabled={disabled}
              style={digitButtonStyle}
              onMouseDown={(e) => {
                if (!disabled) {
                  e.currentTarget.style.transform = 'scale(0.97)';
                }
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              {digit}
            </button>
          ))}
        </div>
      ))}

      <div
        className="numpad__row"
        style={{
          display: 'flex',
          gap: theme.spacing.sm,
          justifyContent: 'center',
        }}
      >
        <button
          className="numpad__digit"
          onClick={() => onDigit(0)}
          disabled={disabled}
          style={digitButtonStyle}
          onMouseDown={(e) => {
            if (!disabled) {
              e.currentTarget.style.transform = 'scale(0.97)';
            }
          }}
          onMouseUp={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          0
        </button>
      </div>

      <div
        className="numpad__actions"
        style={{
          display: 'flex',
          gap: theme.spacing.sm,
          marginTop: theme.spacing.sm,
        }}
      >
        <button
          className="numpad__delete"
          onClick={onDelete}
          disabled={disabled}
          style={{
            ...buttonBaseStyle,
            flex: 1,
            backgroundColor: theme.colours.accent.warning,
            color: theme.colours.text.inverse,
            borderColor: theme.colours.accent.warning,
          }}
          onMouseDown={(e) => {
            if (!disabled) {
              e.currentTarget.style.transform = 'scale(0.97)';
            }
          }}
          onMouseUp={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          DEL
        </button>

        <button
          className="numpad__submit"
          onClick={onSubmit}
          disabled={disabled || !canSubmit}
          style={{
            ...buttonBaseStyle,
            flex: 2,
            backgroundColor: canSubmit && !disabled ? theme.colours.accent.primary : theme.colours.background.tertiary,
            color: canSubmit && !disabled ? theme.colours.text.inverse : theme.colours.text.tertiary,
            borderColor: canSubmit && !disabled ? theme.colours.accent.primary : theme.colours.background.tertiary,
            cursor: !canSubmit || disabled ? 'not-allowed' : 'pointer',
            opacity: !canSubmit || disabled ? 0.5 : 1,
          }}
          onMouseDown={(e) => {
            if (!disabled && canSubmit) {
              e.currentTarget.style.transform = 'scale(0.97)';
            }
          }}
          onMouseUp={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          SUBMIT
        </button>
      </div>
    </div>
  );
}
